/** @jsx isolateComponent */
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { css } from '@emotion/react';
import { Colors, Typography, Img } from '../../../index';
import { Heading, HeadingLevel } from '../../display/Heading';
import {
  AsanaIcon,
  AsanaIconMobile,
  DefaultCompanies,
  DropboxIcon,
  DropboxIconMobile,
  MetaIcon,
  MetaIconMobile,
  ShopifyIcon,
  ShopifyIconMobile,
  SlackIcon,
  SlackIconMobile,
  DefaultCompany,
  DefaultLogoIconType,
} from './logoIcons';
import { Fragment } from 'react';

type ItemProps = {
  logoUrl: string;
  companyName: string;
};

export type Props = {
  /**
   * The title of the `LogoBar`.
   */
  title: React.ReactNode;
  /**
   * Array of items to render as images below the title.
   */
  items: Array<ItemProps>;
  /**
   * Size of the logos. This value will be the width and height of
   * the logo container.
   */
  size?: number;
};

const breakpoints = {
  tablet: 768,
  desktop: 1230,
};

const sanitizeClassName = (size: string | number) => {
  return `${size}`.replace(/[^a-z0-9]/gi, '-');
};

const Styles = labelStyles('LogoBar', {
  root: css`
    display: grid;
    justify-content: center;
    justify-items: center;
    grid-gap: 30px;
  `,
  title: css`
    ${Typography.smallPrint1};
    color: ${Colors.neutral700};
    justify-self: center;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.paragraph3}
    }
  `,
  logosContainer: css`
    display: grid;
    justify-content: center;
    grid-auto-flow: column;
    grid-gap: 40px;

    @media screen and (min-width: ${breakpoints.desktop}px) {
      grid-gap: 80px;
    }
  `,
  logoContainer: css`
    align-self: center;
    display: flex;
    justify-self: center;
  `,
  desktopLogoContainer: css`
    display: none;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      display: block;
    }
  `,
  mobileLogoContainer: css`
    display: block;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      display: none;
    }
  `,
  defaultLogo: (size?: number) => [
    {
      '> svg': {
        height: 'inherit',
        width: 'inherit',
      },
    },
    size &&
      withModifier(sanitizeClassName(`${size}x${size}`), {
        height: size,
        width: size,
      }),
  ],
});

const DefaultLogo = (props: DefaultLogoIconType) => {
  return (
    <span
      css={Styles.defaultLogo(props.size)}
      dangerouslySetInnerHTML={{ __html: props.svg }}
    />
  );
};

const DefaultLogoIcons: Record<
  DefaultCompany,
  { mobile: DefaultLogoIconType; desktop: DefaultLogoIconType }
> = {
  Asana: {
    mobile: AsanaIconMobile,
    desktop: AsanaIcon,
  },
  Dropbox: {
    mobile: DropboxIconMobile,
    desktop: DropboxIcon,
  },
  Meta: {
    mobile: MetaIconMobile,
    desktop: MetaIcon,
  },
  Shopify: {
    mobile: ShopifyIconMobile,
    desktop: ShopifyIcon,
  },
  Slack: {
    mobile: SlackIconMobile,
    desktop: SlackIcon,
  },
};

export const LogoBar = (_props: Props) => {
  const { title, items, size } = _props;

  return (
    <div css={Styles.root}>
      <HeadingLevel>
        <Heading>
          <div css={Styles.title}>{title}</div>
        </Heading>
      </HeadingLevel>
      <ul css={Styles.logosContainer}>
        {items && items.length > 0
          ? items.map(({ logoUrl, companyName }, i) => (
              <li css={Styles.logoContainer} key={i + logoUrl}>
                <Img
                  alt={companyName}
                  borderRadius={0}
                  src={logoUrl}
                  height={size}
                  width={size}
                />
              </li>
            ))
          : DefaultCompanies.map((company) => (
              <Fragment key={company}>
                <li css={[Styles.logoContainer, Styles.desktopLogoContainer]}>
                  <DefaultLogo
                    {...DefaultLogoIcons[company].desktop}
                    size={size}
                  />
                </li>
                <li css={[Styles.logoContainer, Styles.mobileLogoContainer]}>
                  <DefaultLogo
                    {...DefaultLogoIcons[company].mobile}
                    size={size}
                  />
                </li>
              </Fragment>
            ))}
      </ul>
    </div>
  );
};
